/**
 * Forest Theme
 *
 * Natural, earthy green tones inspired by forests.
 */

import type { AppTheme } from './types';

export const forestTheme: AppTheme = {
  name: 'forest',
  displayName: 'Forest',
  description: 'Natural, earthy green tones',

  antdTheme: {
    token: {
      colorPrimary: '#16a34a',
      colorSuccess: '#22c55e',
      colorWarning: '#eab308',
      colorError: '#dc2626',
      colorInfo: '#0d9488',
      colorLink: '#16a34a',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 6,
      borderRadiusLG: 10,
      borderRadiusSM: 4,
      borderRadiusXS: 3,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#f0fdf4',
      colorBorder: '#bbf7d0',
      colorBorderSecondary: '#dcfce7',

      colorText: '#14532d',
      colorTextSecondary: '#166534',
      colorTextTertiary: '#86efac',
      colorTextQuaternary: '#bbf7d0',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#15803d',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#14532d',
        bodyBg: '#f0fdf4',
      },

      Button: {
        borderRadius: 6,
        borderRadiusLG: 8,
        borderRadiusSM: 4,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 10,
        borderRadiusLG: 12,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#f0fdf4',
        headerColor: '#86efac',
        headerBorderRadius: 6,
        borderRadius: 6,
        rowHoverBg: '#f0fdf4',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#86efac',
        linkColor: '#16a34a',
      },

      Segmented: {
        borderRadius: 6,
        itemSelectedBg: '#16a34a',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#86efac',
        itemSelectedColor: '#14532d',
        itemHoverColor: '#166534',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#dcfce7',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#15803d',
    '--layout-sidebar-bg': '#14532d',
    '--layout-content-bg': '#f0fdf4',

    '--surface-0': '#f0fdf4',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#14532d',
    '--text-secondary': '#166534',
    '--text-tertiary': '#86efac',
    '--text-muted': '#bbf7d0',

    '--border-default': '#bbf7d0',
    '--border-subtle': '#dcfce7',
    '--border-strong': '#86efac',

    '--border-color': '#bbf7d0',
    '--border-radius': '6px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#eab308',
    '--accent-cool': '#0d9488',

    '--status-success': '#22c55e',
    '--status-warning': '#eab308',
    '--status-error': '#dc2626',
    '--status-info': '#0d9488',

    '--status-success-subtle': '#ecfdf5',
    '--status-warning-subtle': '#fefce8',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#f0fdfa',

    '--control-bg': '#f0fdf4',
    '--control-border': '#bbf7d0',
    '--control-hover': '#dcfce7',

    '--hover-bg': '#f0fdf4',
    '--active-bg': '#dcfce7',
  },
};
